import React from 'react';
import Swiper from 'swiper';
import {
  Navigation,
  Pagination,
  A11y,
  Autoplay,
  EffectFade,
} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { OverlayCard } from './OverlayCard';
import {
  getDestaqueCarrosselImageProps,
  MEMORIAL_BADGE,
  resolveCarrosselAutoplay,
  type DestaqueCarrosselCard,
} from './helpers';

interface DestaquesCarrosselProps {
  cards: DestaqueCarrosselCard[];
  /** Liga a troca automática de destaque (chave da barra lateral). */
  autoplay?: boolean;
  /** Tempo em cada destaque, em segundos. */
  autoplayDelay?: number;
  isEditMode?: boolean;
  onEditItem?: (index: number) => void;
}

/**
 * Quem pediu menos animação no sistema não recebe troca automática nem o
 * esmaecimento entre os destaques.
 *
 * A consulta é feita na hora de montar o Swiper (e não numa media query no
 * CSS) porque o que precisa ser desligado é o temporizador, não só uma
 * transição: animação suprimida com o carrossel andando sozinho vira troca
 * seca de conteúdo, que é justamente o que incomoda.
 */
const prefereMenosMovimento = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Duração do esmaecimento entre um destaque e outro.
 *
 * Mais longa que os 500ms do carrossel de "Onde o MPF atua?": lá o movimento
 * lateral já sinaliza a troca, aqui só a opacidade muda e o mesmo tempo faria
 * o card parecer piscar. Sob `prefers-reduced-motion` a troca é imediata.
 */
const FADE_MS = 700;

/**
 * As setas ficam no centro vertical: diferente do carrossel de "Onde o MPF
 * atua?", aqui o card é a foto inteira (não há legenda abaixo dela). O estado
 * desabilitado vem do `.swiper-button-disabled` que o Swiper aplica (não do
 * atributo `disabled`) — ver `_capa-overlay-cards.scss`.
 */
const ARROW_CLASS =
  'absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg bg-black/40 text-white transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white';

/**
 * Carrossel da faixa "Destaques".
 *
 * Swiper na API imperativa, como em `OndeAtuaCarousel`: guarda de SSR, trava
 * contra o duplo-monte do StrictMode e `querySelector` escopado ao próprio nó.
 * As classes são PRÓPRIAS (`mpf-swiper-destaques*`) e não reaproveitam as de
 * "Onde o MPF atua?": os dois carrosséis convivem na mesma página e disputariam
 * os mesmos controles.
 *
 * Um slide por vez em qualquer largura — a coluna é estreita (4/12) e o design
 * mostra o card inteiro. O destaque seguinte só entra por ação de quem lê
 * (seta ou ponto), a menos que a redação ligue a troca automática na chave da
 * barra lateral.
 */
export const DestaquesCarrossel: React.FC<DestaquesCarrosselProps> = ({
  cards,
  autoplay,
  autoplayDelay,
  isEditMode,
  onEditItem,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const swiperRef = React.useRef<Swiper | null>(null);
  const initialized = React.useRef(false);
  // A troca automática está ligada neste carrossel? Só então existe o botão
  // de pausar (AB#9144). `pausado` é a escolha de quem lê, feita pelo botão;
  // vive também num ref porque o `pausarAoFocar` precisa consultá-la sem
  // reatar o efeito.
  const [autoplayLigado, setAutoplayLigado] = React.useState(false);
  const [pausado, setPausado] = React.useState(false);
  const pausadoRef = React.useRef(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (initialized.current) return undefined;
    const el = rootRef.current?.querySelector<HTMLElement>(
      '.mpf-swiper-destaques',
    );
    if (!el) return undefined;

    const menosMovimento = prefereMenosMovimento();
    const autoplayConfig = resolveCarrosselAutoplay({
      autoplay,
      autoplayDelay,
      isEditMode,
      total: cards.length,
      prefereMenosMovimento: menosMovimento,
    });

    const paginacaoEl = rootRef.current?.querySelector<HTMLElement>(
      '.mpf-swiper-destaques-pagination',
    );

    /**
     * Fração já decorrida do intervalo, de 0 a 1, publicada como variável CSS.
     *
     * É o que preenche a pílula do destaque em cartaz — ver
     * `_capa-overlay-cards.scss`. Sem troca automática a variável nunca é
     * escrita e a pílula fica chapada, como antes.
     */
    const publicarProgresso = (fracao: number) =>
      paginacaoEl?.style.setProperty(
        '--mpf-destaques-progresso',
        String(fracao),
      );

    /**
     * Só o destaque em cartaz pode receber o foco do teclado.
     *
     * No esmaecimento os demais destaques continuam no DOM, só transparentes:
     * sem isto o Tab percorreria links invisíveis antes de chegar às setas.
     * `inert` tira do Tab e da árvore de acessibilidade. Com `loop` o Swiper
     * duplica slides, então a regra vale por posição (`activeIndex`), não pela
     * classe.
     */
    const isolarSlideAtivo = (sw: Swiper) => {
      (sw.slides || []).forEach((slide, i) => {
        if (i === sw.activeIndex) {
          slide.removeAttribute('inert');
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('inert', '');
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    };

    initialized.current = true;
    setAutoplayLigado(Boolean(autoplayConfig));
    pausadoRef.current = false;
    setPausado(false);
    swiperRef.current = new Swiper(el, {
      modules: [Navigation, Pagination, A11y, Autoplay, EffectFade],
      slidesPerView: 1,
      autoplay: autoplayConfig,
      // Esmaecimento no lugar do deslize lateral: o card ocupa a coluna
      // inteira e não há vizinho aparecendo na borda, então arrastar de lado
      // não comunica nada — a troca é de conteúdo, não de posição.
      effect: 'fade',
      fadeEffect: {
        // Sem o cruzamento, o Swiper só acende o destaque que entra por cima
        // do que sai: enquanto os dois estão opacos aparece a sobreposição
        // das duas fotos, e no fim da transição a troca é seca.
        crossFade: true,
      },
      speed: menosMovimento ? 0 : FADE_MS,
      // Sem `spaceBetween`: com um slide por vez o intervalo nunca aparece, e
      // a margem que ele põe no último slide entra no min-content do
      // `.swiper`. Isso deixa a largura do carrossel num ponto fixo qualquer
      // (`slide = swiper - 16`, `swiper = slide + 16` fecha em qualquer
      // valor), então depois de um resize ela poderia parar longe da coluna.
      // Com um slide só o `loop` do Swiper duplica o slide (ou desiste e trava
      // as setas); melhor deixá-lo desligado nesse caso.
      loop: cards.length > 1,
      // Sem as mensagens, o módulo de a11y sobrescreve o `aria-label` dos
      // nossos botões com os textos padrão dele, em inglês.
      a11y: {
        enabled: true,
        prevSlideMessage: 'Destaque anterior',
        nextSlideMessage: 'Próximo destaque',
        firstSlideMessage: 'Este é o primeiro destaque',
        lastSlideMessage: 'Este é o último destaque',
        paginationBulletMessage: 'Ir para o destaque {{index}}',
      },
      resizeObserver: false,
      navigation: {
        prevEl: el.querySelector<HTMLElement>('.mpf-swiper-destaques-prev'),
        nextEl: el.querySelector<HTMLElement>('.mpf-swiper-destaques-next'),
      },
      pagination: {
        // Os bullets ficam FORA do `.swiper` (que tem `overflow: hidden` e
        // cortaria a linha abaixo do card), por isso `paginacaoEl` vem do
        // `rootRef`: buscar a partir do `el` não os encontraria e a paginação
        // simplesmente não seria montada.
        el: paginacaoEl,
        clickable: true,
      },
      on: {
        // Dispara a cada quadro enquanto o temporizador corre. O terceiro
        // argumento vai de 1 (acabou de trocar) a 0 (vai trocar agora), daí a
        // inversão. Pausado, o Swiper para de emitir e a pílula congela —
        // que é justamente o retorno visual de "pausado".
        autoplayTimeLeft: (_swiper, _restante, progresso) =>
          publicarProgresso(1 - progresso),
        // `disableOnInteraction` encerra a troca automática na primeira seta
        // clicada. Sem zerar aqui, a pílula ficaria parada pela metade para
        // sempre, prometendo um tempo que não corre mais.
        autoplayStop: () => publicarProgresso(0),
        init: isolarSlideAtivo,
        slideChange: isolarSlideAtivo,
        update: isolarSlideAtivo,
      },
    });

    // O `init` do Swiper já correu dentro do construtor; reaplica por garantia
    // com a instância pronta.
    isolarSlideAtivo(swiperRef.current);

    return () => {
      swiperRef.current?.destroy(true, true);
      swiperRef.current = null;
      initialized.current = false;
      paginacaoEl?.style.removeProperty('--mpf-destaques-progresso');
    };
  }, [cards.length, autoplay, autoplayDelay, isEditMode]);

  /**
   * Pausa a troca automática enquanto o foco estiver dentro do carrossel.
   *
   * O `pauseOnMouseEnter` do Swiper só cobre o ponteiro. Quem navega pelo
   * teclado ficaria com o card mudando embaixo do foco — e, ao chegar no botão
   * "Saiba mais", clicaria no destaque errado.
   */
  const pausarAoFocar = React.useCallback((pausar: boolean) => {
    // Quem pausou pelo botão decidiu; o foco entrando e saindo não desfaz.
    if (pausadoRef.current) return;
    // `running` diz que a troca automática está ligada (continua verdadeiro
    // enquanto pausada); sem ela ligada não há o que pausar.
    const autoplayApi = swiperRef.current?.autoplay;
    if (!autoplayApi?.running) return;
    if (pausar) autoplayApi.pause();
    else autoplayApi.resume();
  }, []);

  /**
   * Botão de pausar/retomar a troca automática (AB#9144).
   *
   * `stop()`/`start()`, e não `pause()`/`resume()`: parar de fato zera a
   * pílula de progresso (o Swiper emite `autoplayStop`) e desliga o
   * `running`, então o foco e o mouse deixam de mexer no temporizador até
   * quem lê retomar.
   */
  const alternarPausa = React.useCallback(() => {
    const autoplayApi = swiperRef.current?.autoplay;
    if (!autoplayApi) return;
    const proximo = !pausadoRef.current;
    pausadoRef.current = proximo;
    setPausado(proximo);
    if (proximo) {
      // Com o foco dentro do carrossel o `pausarAoFocar` já deixou o Swiper em
      // `paused`. `stop()` não limpa esse estado e o `start()` seguinte
      // herdaria o congelamento (pílula parada, tempo velho). Retomar antes de
      // parar devolve o estado limpo para o `start()` de "Retomar".
      if (autoplayApi.paused) autoplayApi.resume();
      autoplayApi.stop();
    } else {
      autoplayApi.start();
    }
  }, []);

  if (cards.length === 0) return null;

  return (
    <div
      ref={rootRef}
      className="flex min-w-0 flex-1 flex-col"
      onFocusCapture={() => pausarAoFocar(true)}
      onBlurCapture={() => pausarAoFocar(false)}
    >
      {/* `w-full` não é decorativo: o CSS do Swiper dá `margin-inline: auto`
          ao `.swiper`, e margem automática no eixo transversal desliga o
          esticamento do item flex. Sem largura declarada ele cai em
          `fit-content` — que aqui é o max-content dos slides (o Swiper grava
          largura fixa neles e os deixa `flex-shrink: 0`), então a caixa cresce
          junto com o número de destaques e o `overflow: hidden` não tem o que
          cortar: com dois slides o segundo aparecia fora da faixa. */}
      <div className="swiper mpf-swiper-destaques relative w-full min-w-0 flex-1">
        <div className="swiper-wrapper">
          {cards.map((card, index) => (
            /* Sem `h-auto` aqui (diferente de "Onde o MPF atua?", onde os
               cards têm altura própria): o `height: 100%` que o CSS do Swiper
               dá ao slide é o que fecha a cadeia de alturas definidas até o
               `h-full` do card. */
            <div
              key={card.key}
              className="swiper-slide"
              suppressHydrationWarning
            >
              <OverlayCard
                // Mesmo nível dos cards de iniciativas: a seção que envolve
                // este carrossel é h3.
                as="h4"
                badge={MEMORIAL_BADGE}
                title={card.title}
                description={card.description}
                href={card.href}
                external={card.external}
                alt={card.alt}
                image={getDestaqueCarrosselImageProps(card)}
                // Sem `aspect-*`: aqui a altura vem da coluna (o card
                // acompanha o mosaico ao lado), e proporção junto com
                // `h-full` é contraditório — a altura vence e o
                // `aspect-ratio` é ignorado. O `min-h` cobre o caso da
                // coluna curta, no mobile.
                className="h-full min-h-[20rem]"
                eager={index === 0}
                isEditMode={isEditMode}
                editLabel={`Editar destaque: ${card.title || 'sem título'}`}
                onEdit={
                  onEditItem ? () => onEditItem(card.sourceIndex) : undefined
                }
              />
            </div>
          ))}
        </div>

        {cards.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Destaque anterior"
              data-tooltip="Destaque anterior"
              className={`mpf-swiper-destaques-prev left-2 ${ARROW_CLASS}`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="h-5 w-5"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Próximo destaque"
              data-tooltip="Próximo destaque"
              className={`mpf-swiper-destaques-next right-2 ${ARROW_CLASS}`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="h-5 w-5"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </>
        )}
      </div>

      <div className="mt-4 flex shrink-0 items-center justify-center gap-3">
        <div className="mpf-swiper-destaques-pagination flex items-center justify-center gap-2" />
        {autoplayLigado && (
          <button
            type="button"
            className="mpf-swiper-destaques-pausa flex h-8 w-8 items-center justify-center rounded-full text-[color:var(--primary)] transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary)]"
            aria-pressed={pausado}
            aria-label={
              pausado
                ? 'Retomar a troca automática dos destaques'
                : 'Pausar a troca automática dos destaques'
            }
            onClick={alternarPausa}
          >
            {pausado ? (
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default DestaquesCarrossel;
